import { useState } from 'react';
import { Button, Stack, Text, TextInput } from '@mantine/core';
import { ClientForm, FormVerificationMetadata } from '../../api/types/digitalForms.api.types';
import { verifyZipcode, VerifyZipcodeError } from '../../api/REST/useVerifyZipcode';
import classes from './ZipcodeVerification.module.scss';

interface ZipcodeVerificationProps {
  metadata: FormVerificationMetadata;
  onVerificationSuccess: (clientForm: ClientForm) => void;
}

export const ZipcodeVerification = ({ metadata, onVerificationSuccess }: ZipcodeVerificationProps) => {
  const [zipcode, setZipcode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isLocked, setIsLocked] = useState(false);

  const handleSubmit = async () => {
    if (!zipcode.trim()) return;

    setIsSubmitting(true);
    setErrorMessage(null);

    const minDelay = new Promise((resolve) => setTimeout(resolve, 1000));

    try {
      const [clientForm] = await Promise.all([verifyZipcode({ uuid: metadata.uuid, zipcode: zipcode.trim() }), minDelay]);
      onVerificationSuccess(clientForm);
    } catch (err) {
      await minDelay;
      const error = err as VerifyZipcodeError;

      if (error.isInvalidated) {
        setIsLocked(true);
        return;
      }

      setErrorMessage(error.message || 'Verification failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  if (isLocked) {
    return (
      <div className={classes.lockedContainer}>
        <Stack className={classes.lockedCard}>
          <Text className={classes.lockedTitle}>Form Locked</Text>
          <Text className={classes.lockedMessage}>This form has been locked due to too many failed verification attempts. Please contact the sender for assistance.</Text>
        </Stack>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <Stack className={classes.card}>
        <Text className={classes.instructions}>Please enter the ZIP code associated with your account to access this form.</Text>
        <div className={classes.inputWrapper}>
          <TextInput
            placeholder="Enter ZIP code"
            value={zipcode}
            onChange={(e) => setZipcode(e.currentTarget.value)}
            onKeyDown={handleKeyDown}
            inputMode="numeric"
            maxLength={10}
            classNames={{ input: classes.input }}
            aria-label="ZIP code"
            disabled={isSubmitting}
          />
        </div>
        {errorMessage ? <Text className={classes.errorMessage}>{errorMessage}</Text> : null}
        <Button onClick={handleSubmit} loading={isSubmitting} disabled={!zipcode.trim()} fullWidth size="md">
          Verify
        </Button>
      </Stack>
    </div>
  );
};
